/**
 * Transfer pricing (FT19-U33, FSD FIN-FR-031).
 *
 * The wiring packet spreads `TP_ROUTES` into the router and adds the nav item
 * described by `TP_NAV_ITEM` to p02 section s-2.1. The policy detail route is
 * reached from the list (and the Approvals Inbox preview), so it needs a nav
 * alias onto `interco-transfer-pricing`, not a nav item of its own.
 *
 * Both routes are gated on `tp.policy.read`, which every GET of
 * `internal/tp` declares (and the read-only price quote too). Drafting,
 * editing, submitting and superseding are checked on the action.
 */

import type { RouteRecordRaw } from 'vue-router'

import { TP_PERMISSIONS } from '@/modules/interco/transfer-pricing/permissions'

export const TP_PATHS = {
  root: '/finance/interco/transfer-pricing',
  policy: (id: number | string): string => `/finance/interco/transfer-pricing/${id}`,
  journal: (id: number | string): string => `/gl/journal-entries/${id}`,
} as const

export const TP_TABS = ['policies', 'quote', 'departures'] as const

export type TpTab = (typeof TP_TABS)[number]

export const TP_ROUTES: RouteRecordRaw[] = [
  {
    path: '/finance/interco/transfer-pricing',
    name: 'interco-transfer-pricing',
    component: () => import('@/modules/interco/transfer-pricing/TransferPricingView.vue'),
    meta: { titleKey: 'interco.tp.routeTitle', permission: TP_PERMISSIONS.read },
  },
  {
    path: '/finance/interco/transfer-pricing/:id(\\d+)',
    name: 'interco-transfer-pricing-policy',
    component: () => import('@/modules/interco/transfer-pricing/TpPolicyView.vue'),
    meta: { titleKey: 'interco.tp.policy.routeTitle', permission: TP_PERMISSIONS.read },
  },
]

/** The nav item INT adds to p02 section s-2.1 (shape of nav/parts items). */
export const TP_NAV_ITEM = {
  id: 'interco-transfer-pricing',
  labelKey: 'interco.tp.navLabel',
  to: TP_PATHS.root,
  icon: 'scale',
  permission: TP_PERMISSIONS.read,
  code: 'FIN-FR-031',
  section: 's-2.1',
  descriptionKey: 'interco.tp.navDescription',
} as const
